// modules/marchee-belt-to-test.js

/**
 * Inizializza il Marquee Belt (marquee slider) accessibile e prefer-reduced-motion friendly.
 * VERSIONE TEST: Correzione glitch sub-pixel e isolamento istanze.
 */

export function initMarqueeBelt(container) {
    if (!container) {
        console.warn("[marquee-belt] Nessun container passato all'inizializzazione.");
        return;
    }

    const wrapper = container.querySelector(".marquee-belt-wrapper");
    const slidesWrapper = container.querySelector(".marquee-belt-slides-wrapper");
    if (!wrapper || !slidesWrapper) {
        console.warn("[marquee-belt] Struttura HTML mancante.");
        return;
    }

    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const dir = (container.dataset.dir || "left").toLowerCase();
    const speed = parseFloat(container.dataset.speed) || 20;
    const gap = parseFloat(container.dataset.space) || 40;
    const prefix = container.dataset.prefix || "px";
    const pauseHover = container.dataset.pauseHover !== "false";
    const pauseFocus = container.dataset.pauseFocus !== "false";

    setup(mq.matches);
    mq.addEventListener("change", e => setup(e.matches));

    function setup(reduced) {
        cleanup(container);

        if (reduced) {
            container.classList.add("has-reduced-motion");
            container.style.overflowX = "auto";
            container.style.overflowY = "hidden";
            container.style.scrollBehavior = "smooth";

            slidesWrapper.style.gap = `0 ${gap}${prefix}`;
            slidesWrapper.style.minWidth = "max-content";
            slidesWrapper.style.flexShrink = "0";
            enableHorizontalUX(container);
            return;
        }

        container.classList.remove("has-reduced-motion");
        container.style.overflow = "hidden";

        // Prepariamo lo stile base prima della misurazione
        slidesWrapper.style.display = "flex";
        slidesWrapper.style.flexShrink = "0";
        slidesWrapper.style.gap = `0 ${gap}${prefix}`;
        slidesWrapper.style.margin = `0 ${gap / 2}${prefix}`;
        slidesWrapper.style.transform = "none";
        slidesWrapper.style.animation = "none";

        // Misurazione precisa sub-pixel
        const rect = slidesWrapper.getBoundingClientRect();
        const totalWidth = rect.width + gap; 

        // Duplicazione
        const clone = slidesWrapper.cloneNode(true);
        clone.setAttribute("aria-hidden", "true"); // Importante per l'accessibilità
        wrapper.appendChild(clone);

        // Generazione ID univoco per questa istanza
        const animId = Math.random().toString(36).slice(2, 8);
        const animName = `marquee_${animId}`;
        container.__marqueeAnimName = animName; // Salviamo per cleanup selettivo

        const keyframes = `
            @keyframes ${animName} {
                0% { transform: translate${axis(dir)}(0) translateZ(0); }
                100% { transform: translate${axis(dir)}(${sign(dir)}${totalWidth.toFixed(4)}px) translateZ(0); }
            }
        `;

        const styleEl = document.createElement("style");
        styleEl.dataset.marquee = animName;
        styleEl.textContent = keyframes;
        document.head.appendChild(styleEl);

        wrapper.style.display = "flex";
        wrapper.style.animation = `${animName} ${speed}s linear infinite`;
        wrapper.style.willChange = "transform";

        if (pauseHover) {
            container.addEventListener("mouseenter", pause);
            container.addEventListener("mouseleave", play);
        }
        if (pauseFocus) {
            container.addEventListener("focusin", pause);
            container.addEventListener("focusout", play);
        }

        function pause() { wrapper.style.animationPlayState = "paused"; }
        function play() { wrapper.style.animationPlayState = "running"; }

        const onResize = () => {
            cleanup(container);
            setup(false);
        };
        container.__resizeHandler = onResize;
        window.addEventListener("resize", onResize, { passive: true });
    }
}

function axis(dir) {
    return (dir === "up" || dir === "down") ? "Y" : "X";
}

function sign(dir) {
    return (dir === "right" || dir === "down") ? "+" : "-";
}

function cleanup(container) {
    // Rimuoviamo SOLO lo stile di questa istanza
    if (container.__marqueeAnimName) {
        const styleEl = document.querySelector(`style[data-marquee="${container.__marqueeAnimName}"]`);
        if (styleEl) styleEl.remove();
    }

    const wrapper = container.querySelector(".marquee-belt-wrapper");
    if (wrapper) {
        wrapper.style.animation = "none";
        const clones = wrapper.querySelectorAll(".marquee-belt-slides-wrapper:not(:first-child)");
        clones.forEach(c => c.remove());
        
        const original = wrapper.querySelector(".marquee-belt-slides-wrapper");
        if (original) {
            original.style.margin = "";
            original.style.gap = "";
            original.style.flexShrink = "";
        }
    }

    if (container.__resizeHandler) {
        window.removeEventListener("resize", container.__resizeHandler);
        container.__resizeHandler = null;
    }
}

function enableHorizontalUX(scroller) {
    if (scroller.__nm_scrollInit) return;
    scroller.__nm_scrollInit = true;

    if (!scroller.hasAttribute("tabindex")) scroller.setAttribute("tabindex", "0");
    
    scroller.addEventListener("wheel", e => {
        if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
            scroller.scrollLeft += e.deltaY;
            e.preventDefault();
        }
    }, { passive: false });

    scroller.addEventListener("keydown", e => {
        const step = 80;
        if (e.key === "ArrowRight") { scroller.scrollLeft += step; e.preventDefault(); }
        if (e.key === "ArrowLeft") { scroller.scrollLeft -= step; e.preventDefault(); }
    });

    let dragging = false, startX = 0, startLeft = 0;
    scroller.addEventListener("pointerdown", e => {
        dragging = true;
        startX = e.clientX;
        startLeft = scroller.scrollLeft;
        scroller.setPointerCapture(e.pointerId);
    });
    scroller.addEventListener("pointermove", e => {
        if (!dragging) return;
        scroller.scrollLeft = startLeft - (e.clientX - startX);
    });
    scroller.addEventListener("pointerup", e => {
        dragging = false; 
        scroller.releasePointerCapture(e.pointerId);
    });
    scroller.addEventListener("pointercancel", () => (dragging = false));
}
